import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Calendar, 
  DollarSign, 
  Package, 
  Clock,
  Activity,
  User,
  Edit,
  LogIn,
  Trash,
  Plus,
  TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#10b981',
  completed: '#6366f1',
  rejected: '#ef4444',
  cancelled: '#94a3b8'
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, chartsRes, bookingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/charts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/recent-bookings?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/activity-logs?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAnalytics(analyticsRes.data);
      setChartData(chartsRes.data);
      setRecentBookings(bookingsRes.data.recent_bookings || []);
      setActivityLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => STATUS_COLORS[status] || '#64748b';

  const getPaymentStatusColor = (status) => {
    const colors = { paid: '#10b981', unpaid: '#f59e0b', initiated: '#3b82f6', refunded: '#ef4444' };
    return colors[status] || '#64748b';
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn size={14} />;
      case 'create': return <Plus size={14} />;
      case 'update': return <Edit size={14} />;
      case 'delete': return <Trash size={14} />;
      default: return <Activity size={14} />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login': return '#3b82f6';
      case 'create': return '#10b981';
      case 'update': return '#f59e0b';
      case 'delete': return '#ef4444';
      default: return '#64748b';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div style={{ background: '#1a1a1a', padding: '12px 16px', border: 'none', fontSize: '13px', color: '#fff' }}>
        <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{label}</p>
        {payload.map((item, i) => (
          <p key={i} style={{ margin: '2px 0', color: item.color }}>
            {item.name}: {item.name === 'Revenue' ? `$${item.value.toFixed(2)}` : item.value}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container"><p>Loading dashboard...</p></div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieData = chartData?.status_breakdown
    ? Object.entries(chartData.status_breakdown)
        .filter(([_, v]) => v > 0)
        .map(([key, value]) => ({ name: key.charAt(0).toUpperCase() + key.slice(1), value }))
    : [];

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <p className="dashboard-subtitle">Welcome back! Here's an overview of your business.</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" data-testid="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
            <Calendar size={24} style={{ color: '#6366f1' }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Bookings</div>
            <div className="stat-value">{analytics?.total_bookings || 0}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
            <DollarSign size={24} style={{ color: '#10b981' }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">${analytics?.total_revenue?.toFixed(2) || '0.00'}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
            <Clock size={24} style={{ color: '#f59e0b' }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Bookings</div>
            <div className="stat-value">{analytics?.pending_bookings || 0}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
            <Package size={24} style={{ color: '#8b5cf6' }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Active Packages</div>
            <div className="stat-value">{analytics?.active_packages || 0}</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {chartData && (
        <div className="charts-section" data-testid="charts-section">
          {/* Revenue Chart */}
          <div className="chart-card chart-wide">
            <div className="chart-header">
              <h3><TrendingUp size={18} /> Revenue Trend</h3>
              <span className="chart-subtitle">Last 6 months</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#8a8a8a' }} />
                <YAxis tick={{ fontSize: 12, fill: '#8a8a8a' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Trends */}
          <div className="chart-card chart-wide">
            <div className="chart-header">
              <h3><Calendar size={18} /> Booking Trends</h3>
              <span className="chart-subtitle">Last 6 months</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#8a8a8a' }} />
                <YAxis tick={{ fontSize: 12, fill: '#8a8a8a' }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="bookings" name="Total" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="confirmed" name="Confirmed" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="charts-row">
            {/* Status Breakdown Pie */}
            {pieData.length > 0 && (
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Booking Status</h3>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={STATUS_COLORS[entry.name.toLowerCase()] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" formatter={(value) => <span style={{ fontSize: '12px', color: '#6b7280' }}>{value}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Top Packages */}
            {chartData.top_packages?.length > 0 && (
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Top Packages</h3>
                </div>
                <div className="top-packages-list">
                  {chartData.top_packages.map((pkg, i) => (
                    <div key={i} className="top-package-item">
                      <div className="top-package-rank">{i + 1}</div>
                      <div className="top-package-info">
                        <div className="top-package-name">{pkg.name}</div>
                        <div className="top-package-stats">
                          {pkg.bookings} booking{pkg.bookings !== 1 ? 's' : ''} &middot; ${pkg.revenue.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Bookings */}
      <div className="recent-section">
        <h3>Recent Bookings</h3>
        {recentBookings.length > 0 ? (
          <div className="bookings-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Package</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <div className="booking-name">{booking.customer_name}</div>
                      <div className="booking-email">{booking.customer_email}</div>
                    </td>
                    <td>{booking.package_name || booking.event_type}</td>
                    <td>{booking.event_date || 'Not specified'}</td>
                    <td>
                      <span className="status-badge" style={{
                        background: `${getStatusColor(booking.status)}20`,
                        color: getStatusColor(booking.status)
                      }}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      <span className="payment-badge" style={{
                        background: `${getPaymentStatusColor(booking.payment_status)}20`,
                        color: getPaymentStatusColor(booking.payment_status)
                      }}>
                        {booking.payment_status || 'unpaid'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data"><p>No recent bookings</p></div>
        )}
      </div>

      {/* Activity Logs */}
      <div className="activity-section">
        <h3>Recent Activity</h3>
        {activityLogs.length > 0 ? (
          <div className="activity-list">
            {activityLogs.map((log, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon" style={{
                  background: `${getActionColor(log.action)}15`,
                  color: getActionColor(log.action)
                }}>
                  {getActionIcon(log.action)}
                </div>
                <div className="activity-content">
                  <div className="activity-description">
                    <strong>{log.admin_email}</strong>{' '}
                    <span className="activity-action">{log.action}</span>
                    {log.resource_type && <> {log.resource_type}</>}
                    {log.details && <span className="activity-details"> - {log.details}</span>}
                  </div>
                  <div className="activity-time">{formatTimestamp(log.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-data"><p>No recent activity</p></div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
